import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { interval, Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PaceService {
  private excludedRoutes: string[] = [
    '/service/dashboard',
    '/service/settings/call-trace',
    // Add more routes to exclude here
  ];

  private cleanupSubscription: Subscription | null = null;
  private paceDisabled = false;

  constructor(private router: Router) {
    // Listen for route changes
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      if (this.isExcludedRoute(event.urlAfterRedirects)) {
        this.startCleanup();
      } else {
        this.stopCleanup();
        this.enablePace(); // Re-enable Pace for non-excluded routes
      }
    });

    // Initial check
    if (this.isCurrentRouteExcluded()) {
      this.startCleanup();
    }
  }

  private isCurrentRouteExcluded(): boolean {
    return this.isExcludedRoute(this.router.url);
  }

  private isExcludedRoute(url: string): boolean {
    return this.excludedRoutes.some(route => url.includes(route));
  }

  private startCleanup(): void {
    // Immediate cleanup
    this.cleanupPace();

    // Set up interval for continuous cleanup
    if (!this.cleanupSubscription) {
      this.cleanupSubscription = interval(1000).subscribe(() => this.cleanupPace());
    }
  }

  private stopCleanup(): void {
    if (this.cleanupSubscription) {
      this.cleanupSubscription.unsubscribe();
      this.cleanupSubscription = null;
    }
  }

  private enablePace(): void {
    try {
      // Only restore if we previously disabled Pace
      if (this.paceDisabled && window['Pace'] && window['Pace']._originalStart) {
        // Restore the original start method
        window['Pace'].start = window['Pace']._originalStart;
        delete window['Pace']._originalStart;

        // Reset our tracking flag
        this.paceDisabled = false;

        // Remove any custom styles we might have added
        const customStyleElement = document.getElementById('pace-disable-style');
        if (customStyleElement && customStyleElement.parentNode) {
          customStyleElement.parentNode.removeChild(customStyleElement);
        }

        // Trigger a restart to make sure Pace is activated again
        try {
          if (typeof window['Pace'].restart === 'function') {
            window['Pace'].restart();
          } else {
            window['Pace'].start();
          }
        } catch (err) {
          console.warn('Error restarting Pace:', err);
        }
      }
    } catch (err) {
      console.warn('Error re-enabling Pace:', err);
    }
  }

  private cleanupPace(): void {
    try {
      // 1. Remove all pace-running classes from body
      document.body.className = document.body.className.replace(/pace-running/g, '').trim();

      // 2. Remove any block-ui-overlay elements ONLY on excluded pages
      const overlays = document.querySelectorAll('.block-ui-overlay');
      overlays.forEach(overlay => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      });

      // 3. Remove any pace elements
      const paceElements = document.querySelectorAll('.pace, .pace-progress, .pace-inactive, .pace-running');
      paceElements.forEach(element => {
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      });

      // 4. Ensure body is scrollable
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';

      // 5. If pace exists, stop it
      if (window['Pace']) {
        window['Pace'].stop();

        // Prevent pace from restarting
        if (!window['Pace']._originalStart) {
          window['Pace']._originalStart = window['Pace'].start;
          window['Pace'].start = function () {
            return;
          };
          this.paceDisabled = true;
        }
      }
    } catch (err) {
      console.warn('Error in pace cleanup:', err);
    }
  }
}
